import { AllowedEvent } from '@fhs-legacy/frontend/src/state/crm-events/types';

import { PaymentOrchestrator, PaymentPipelineError, PaymentPipelineResult } from '../../../payment';
import { isPaymentCancelled } from '../../../payment/payment-cancelled-codes';
import { usePaymentFlowStore } from '../../../state/payment-stores/payment-flow-store';

import {
  clearDigitalWalletDetailsFromContext,
  getDefaultRetryStep,
  getDigitalWalletRetryStep,
  isCommitFailureRetry,
  isDigitalWalletPaymentError,
  isRetryablePaymentError,
  needsFreshPipelineContextOnRetry,
} from './payment-retry-utils';

interface HandlePlaceOrderDependencies {
  lastResultRef: React.MutableRefObject<PaymentPipelineResult | null>;
  orchestratorRef: React.MutableRefObject<PaymentOrchestrator>;
  buildPipelineContext: () => any;
  setShouldSkipPriceOrder: (skip: boolean) => void;
  setIsProcessing: (processing: boolean) => void;
  setCurrentError: (error: PaymentPipelineError | null) => void;
  logRBIEvent: (event: AllowedEvent) => void;
}

// Shared across the Place Order button and gift-card reload sheet so a second entry cannot
// start another commit while one is already running.
let isPlaceOrderInFlight = false;

export async function handlePlaceOrder({
  lastResultRef,
  orchestratorRef,
  setCurrentError,
  setIsProcessing,
  setShouldSkipPriceOrder,
  buildPipelineContext,
  logRBIEvent,
}: HandlePlaceOrderDependencies) {
  if (isPlaceOrderInFlight) {
    return;
  }
  isPlaceOrderInFlight = true;

  const {
    retryInfo: existingRetryInfo,
    setRetryInfo,
    setCurrentState,
    setIsCommitRetry,
  } = usePaymentFlowStore.getState();
  const lastResult = lastResultRef.current;
  const isRetry = !!lastResult?.error?.retryFromStep;
  const isCommitRetryAttempt =
    isRetry &&
    (lastResult?.error?.retryFromStep === 'CommitOrderStep' ||
      isCommitFailureRetry(lastResult?.error?.code));
  let orderSubmitted = false;

  if (isCommitRetryAttempt) {
    setIsCommitRetry(true);
  }

  orchestratorRef.current.setCRMLogger(logRBIEvent);

  try {
    if (!isRetry && !getPipelineContext(buildPipelineContext, setCurrentError)) {
      return;
    }

    setCurrentError(null);
    setIsProcessing(true);

    const needsFreshContext =
      isRetry && needsFreshPipelineContextOnRetry(lastResult.error?.code);

    let result: PaymentPipelineResult;
    if (
      isRetry &&
      !needsFreshContext &&
      lastResult != null &&
      lastResult.context != null &&
      lastResult.error != null &&
      lastResult.error.retryFromStep != null
    ) {
      setRetryInfo(null);
      setCurrentState('idle');
      const retryContext = clearDigitalWalletDetailsFromContext(lastResult.context);
      const retryFromStep = getDigitalWalletRetryStep(
        retryContext.paymentMethod,
        lastResult.error.retryFromStep
      );
      result = await orchestratorRef.current.execute(retryContext, {
        skipToStep: retryFromStep,
      });
    } else {
      const pipelineContext = getPipelineContext(buildPipelineContext, setCurrentError);
      if (!pipelineContext) {
        if (isRetry && existingRetryInfo) {
          setRetryInfo(existingRetryInfo);
        }
        return;
      }
      setRetryInfo(null);
      setCurrentState('idle');
      result = await orchestratorRef.current.execute(
        clearDigitalWalletDetailsFromContext(pipelineContext)
      );
    }

    lastResultRef.current = result;

    if (result.success) {
      orderSubmitted = true;
      handleSuccess(setShouldSkipPriceOrder, setRetryInfo, setCurrentError);
      return;
    }

    handleError(result, setCurrentError, setRetryInfo);
    if (!isPaymentCancelled(result.error?.code)) {
      setCurrentState('error');
    }

    if (isDigitalWalletPaymentError(result.error?.code) || isPaymentCancelled(result.error?.code)) {
      lastResultRef.current = null;
    }
  } catch (error) {
    handleUnexpectedError(error, setCurrentError, setRetryInfo);
    setCurrentState('error');
  } finally {
    isPlaceOrderInFlight = false;
    usePaymentFlowStore.getState().setIsCommitRetry(false);
    if (!orderSubmitted) {
      setIsProcessing(false);
    }
  }
}

function getPipelineContext(
  buildPipelineContext: HandlePlaceOrderDependencies['buildPipelineContext'],
  setCurrentError: HandlePlaceOrderDependencies['setCurrentError']
) {
  const pipelineContext = buildPipelineContext();

  if (!pipelineContext) {
    setCurrentError({
      code: 'CONTEXT_NOT_READY',
      message: 'Pipeline context is not ready',
      userMessage:
        'Unable to process payment. Please check your payment details and try again. Error Code: (Pipeline Context Readiness)',
    });
    return null;
  }

  return pipelineContext;
}

function handleSuccess(
  setShouldSkipPriceOrder: (skip: boolean) => void,
  setRetryInfo: (info: any) => void,
  setCurrentError: (error: PaymentPipelineError | null) => void
) {

  setShouldSkipPriceOrder(true);
  setRetryInfo(null);
  setCurrentError(null);
}

function handleError(
  result: PaymentPipelineResult,
  setCurrentError: (error: PaymentPipelineError | null) => void,
  setRetryInfo: (info: any) => void
) {
  const errorCode = result.error?.code;

  if (isPaymentCancelled(errorCode)) {
    setCurrentError(null);
    setRetryInfo(null);
    return;
  }

  setCurrentError(result.error ?? null);

  if (isDigitalWalletPaymentError(errorCode)) {
    return;
  }

  const retryFromStep = result.error?.retryFromStep ?? getDefaultRetryStep(errorCode);

  if (retryFromStep && isRetryablePaymentError(errorCode)) {
    setRetryInfo({
      canRetry: true,
      retryFromStep,
      errorCode: result.error?.code ?? 'UNKNOWN_ERROR',
      errorMessage: result.error?.message ?? 'Unknown payment error',
      failedStep: result.failedStep,
    });
  }
}

function handleUnexpectedError(
  error: unknown,
  setCurrentError: (error: PaymentPipelineError | null) => void,
  setRetryInfo: (info: any) => void
) {
  setCurrentError({
    code: 'UNEXPECTED_ERROR',
    message: error instanceof Error ? error.message : 'Unknown error',
    userMessage:
      'An unexpected error occurred. Please try again. Error Code: (Unexpected Orchestrator Error)',
  });
  setRetryInfo(null);
}
